import React, { CSSProperties } from "react";
import { cn } from "@/lib/utils";

/** Figma's `text-box-trim: cap alphabetic`, so gaps match the frame (Chrome 133+, Safari 18.2+). */
export const CAP_TRIM = "[text-box-trim:trim-both] [text-box-edge:cap_alphabetic]";

/**
 * Cap-trimmed gradient titles: `background-clip: text` only paints inside the box, and cap-trim
 * puts the box edges at cap height/baseline, so ascenders and descenders would lose their fill.
 * Pads the painted area on both sides; the negative margins keep Figma's spacing.
 */
export const GRADIENT_TRIM_PAD = "pt-[0.15em] -mt-[0.15em] pb-[0.3em] -mb-[0.3em]";

/** Display face; spans need it explicitly because of the global `span { font-family }` rule. */
export const DISPLAY = "font-helvetica-lt font-medium not-italic";

/** Text filled with a gradient that fades the last letters, as in every Figma title. */
export function gradientText(angle: number, from: string, fromStop: string, to: string, toStop: string): CSSProperties {
  return {
    backgroundImage: `linear-gradient(${angle}deg, ${from} ${fromStop}, ${to} ${toStop})`,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
  };
}

/** The Q of the IQ mark, light-green gradient (Figma chip logo). */
export function QMark({ className }: { className?: string }) {
  // eslint-disable-next-line @next/next/no-img-element
  return <img src="/elite/chip-q.svg" alt="" aria-hidden="true" className={className} />;
}

export function ArrowRight({ className }: { className?: string }) {
  // eslint-disable-next-line @next/next/no-img-element
  return <img src="/elite/arrow-btn.svg" alt="" aria-hidden="true" className={className} />;
}

/**
 * The label pill above every title: dark square with the Q, then the text.
 * `glass` sits on the light sections, `solid` on the black one.
 */
export function EliteChip({
  label,
  variant,
  className,
}: {
  label?: string;
  variant: "glass" | "solid";
  className?: string;
}) {
  if (!label) return null;
  return (
    <div
      className={cn(
        "flex w-fit items-center gap-[1.25rem] rounded-[1rem] border border-solid border-black/10 py-[0.375rem] pr-[2.5rem] pl-[0.375rem]",
        variant === "glass" ? "bg-black/[0.04] backdrop-blur-[0.525rem]" : "bg-white",
        className,
      )}>
      <div className="relative flex h-[4.75rem] w-[4.75rem] shrink-0 items-center justify-center rounded-[0.5rem] bg-[#0b0b0b] shadow-[inset_0_-0.125rem_0.4125rem_0_rgba(0,0,0,0.25)]">
        <QMark className="h-[1.934rem] w-[2rem]" />
      </div>
      <p className={cn("font-geist text-[1.75rem] leading-[1.146] font-medium tracking-[0.035rem] whitespace-nowrap text-black uppercase", CAP_TRIM)}>
        {label}
      </p>
    </div>
  );
}

/**
 * The green gradient button with its inner glow and the prototype's light: a diagonal beam crosses
 * the button every 4.2s, faster (1.4s) on hover. Pair the label with <SheenLabel> and the arrow
 * with SHEEN_ARROW. Explicit animation-* properties: tailwindcss-animate makes `duration-[…]` ambiguous.
 */
export const GREEN_BUTTON = cn(
  "group relative isolate overflow-hidden bg-[linear-gradient(92deg,#9ee3b0_15.146%,#d9ffe3_95.464%)] shadow-[inset_0_0_1.7375rem_0_rgba(62,158,87,0.58)]",
  "after:pointer-events-none after:absolute after:top-[-60%] after:bottom-[-60%] after:left-[-70%] after:z-[1] after:w-[52%] after:-skew-x-[18deg] after:bg-[linear-gradient(100deg,transparent_0%,rgba(255,255,255,0.34)_50%,transparent_100%)] after:content-['']",
  "after:animate-elite-sheen hover:after:[animation-duration:1.4s] motion-reduce:after:animate-none",
);

/** Nav CTA: its light runs 1.2s after the form buttons, and it brightens on hover (the prototype's `.cta`). */
export const SHEEN_LATE =
  "after:[animation-delay:1.2s] [&_.sheen-label]:[animation-delay:1.2s] transition-[filter] [transition-duration:300ms] hover:brightness-[1.06]";

/** Label text lit by the same light as it passes (black base, like the prototype's `.lz`). */
export function SheenLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="sheen-label animate-elite-sheen-text bg-[linear-gradient(100deg,#000_0%,#000_38%,rgba(255,255,255,0.5)_50%,#000_62%,#000_100%)] bg-[length:260%_100%] bg-[position:120%_0] bg-clip-text text-transparent group-hover:[animation-duration:1.4s] motion-reduce:animate-none motion-reduce:text-black">
      {children}
    </span>
  );
}

/** The button arrow nudges forward on hover. */
export const SHEEN_ARROW =
  "transition-transform [transition-duration:350ms] [transition-timing-function:cubic-bezier(0.22,0.61,0.36,1)] group-hover:translate-x-[0.375rem]";

/** "IQ" wordmark + "Elite". `light` for the black nav, `dark` for the footer. */
export function EliteLogo({ tone, className }: { tone: "light" | "dark"; className?: string }) {
  const light = tone === "light";
  return (
    <div className={cn("grid place-items-start", className)}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={light ? "/elite/nav-iq.svg" : "/elite/footer-iq.svg"}
        alt="IQ"
        className={cn(
          "col-start-1 row-start-1",
          light ? "h-[2.4368rem] w-[2.8932rem]" : "h-[2.4056rem] w-[2.8562rem]",
        )}
      />
      <p
        className={cn(
          "col-start-1 row-start-1 font-geist leading-[1.05] font-bold whitespace-nowrap",
          CAP_TRIM,
          light
            ? "ml-[3.2925rem] bg-[linear-gradient(180deg,#9ee3b0,#d9ffe3)] bg-clip-text text-[3.1476rem] tracking-[-0.0944rem] text-transparent"
            : "ml-[3.25rem] text-[3.1075rem] tracking-[-0.0932rem] text-[#51a667]",
        )}>
        Elite
      </p>
    </div>
  );
}
